import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { ExternalLink, Lock } from "lucide-react";
import { errorMessage, syncPilotDraft } from "@/api/email";
import { Button } from "@/components/ui/button";
import { canSyncPilotDraft } from "@/lib/pilot";
import { cn } from "@/lib/utils";
import { useWorkspace } from "@/pages/email/CampaignWorkspace";

// Kept unchanged for the Sequence canvas; the commercial Review never mounts it.
export function DraftSyncPanel({ compact = false }: { compact?: boolean }) {
  const { campaign, slug, access, external, reload } = useWorkspace();
  const [busy, setBusy] = useState(false);
  const receipt = campaign.pilot_sync;
  const allowed = canSyncPilotDraft(access, campaign, external);
  async function sync() {
    setBusy(true);
    try {
      const result = await syncPilotDraft(slug, campaign.id);
      if (result.status === "COMPLETED") toast.success("Drafts synchronized. Nothing launched.");
      else toast.warning("Partial draft sync: review the recorded failures.");
      await reload();
    } catch (err) {
      toast.error(errorMessage(err, "Draft sync could not complete. Re-read its checkpoint before retrying."));
      await reload();
    } finally { setBusy(false); }
  }
  if (compact) return <div className="flex items-center gap-3">
    {receipt?.provider_url && <a href={receipt.provider_url} target="_blank" rel="noreferrer" className="text-xs text-slate-500 underline">Lemlist ↗</a>}
    {allowed && <Button size="sm" variant="outline" disabled={busy} onClick={() => void sync()}>{busy ? "Synchronisation…" : "Synchroniser les brouillons"}</Button>}
  </div>;
  return <Panel>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h3 className="font-semibold text-slate-900">Lemlist · review drafts only</h3>
      {allowed && <Button size="sm" variant="outline" disabled={busy} onClick={() => void sync()}>
        <Lock className="h-3.5 w-3.5" />{busy ? "Synchronizing drafts…" : "Sync drafts to Lemlist"}
      </Button>}
    </div>
    <p className="mt-2 text-xs text-slate-600">No sender, no auto-launch, no activation permission.
      Personalized J0 drafts pin the existing company A/B allocation; native random assignment is not run.
      Complete copy stays in Review; evidence and unapproved commercial decisions remain internal.</p>
    {receipt ? <div className="mt-3 space-y-2 text-xs">
      <p>Sync: <strong>{receipt.status}</strong> · {receipt.updated_at ?? "Check date unavailable"}</p>
      <p>Leads imported: {receipt.leads_imported ?? "Not verified"} · Steps: {receipt.steps ?? "Not verified"}
        {" · "}Full personalizations confirmed: {receipt.personalizations_verified ?? "Not verified"}</p>
      {receipt.safety && <p>Measured Lemlist state: {receipt.safety.campaign_status}
        {" · "}Launched leads: {receipt.safety.launched_leads}
        {" · "}Scheduled emails: {receipt.safety.scheduled_emails}
        {" · "}Sent emails: {receipt.safety.sent_emails}
        {" · "}Senders: {receipt.safety.senders}</p>}
      {receipt.provider_url && <a className="inline-flex items-center gap-1 font-medium text-blue-700 underline"
        href={receipt.provider_url} target="_blank" rel="noreferrer">Open campaign in Lemlist <ExternalLink className="h-3 w-3" /></a>}
      {receipt.failures.map((f, i) => <p key={i} className="text-rose-700">{f.contact_id ? `Contact ${f.contact_id}: ` : ""}{f.reason}</p>)}
    </div> : <p className="mt-3 text-xs text-slate-500">No Lemlist synchronization receipt yet. Provider state not checked.</p>}
  </Panel>;
}

function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return <section className={cn("rounded-plane bg-white px-4 py-3 shadow-plane", className)}>{children}</section>;
}

